/** 项目 HTML 落地页：生成、下载、发布与投放标记；默认导出严格校验当前输入指纹，失败只重试失败子项。 */
import type { LandingPageDeliveryStatus } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  type ProjectDetail,
  type ProjectTask,
  landingProjectQueryKey,
  locateLandingProjectTaskPage,
  useLandingProduct,
  useLandingProjectTasks,
  useLandingTemplates,
} from '~/data/queries';
import { landingPagesApi } from '~/hooks/use-landing-pages';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { ContextMenu } from '~/ui/ContextMenu';
import { CopyableCode } from '~/ui/CopyableCode';
import { ConfirmDialog } from '~/ui/Dialog';
import { Pagination } from '~/ui/Pagination';
import { Spinner } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { LandingAssetImage } from './LandingAssetImage';
import '../LandingTasksPanel.css';
import { LandingPageDeliveryCell, LandingPageDownloadCell } from './LandingPageDeliveryCells';
import {
  ProjectState,
  projectDownloadAllowed,
  projectHtmlReadinessReason,
  projectPhaseLabel,
} from './landing-project-display';
export function LandingProjectLandingPagesPanel({
  project,
  onExecution,
  focusTaskId = null,
}: {
  project: ProjectDetail;
  onExecution: (id: string) => void;
  /** 从素材卡片跳转时要定位并高亮的落地页（任务 UUID）。 */
  focusTaskId?: string | null;
}) {
  const [page, setPage] = useState(1);
  const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
  const [selection, setSelection] = useState(new Map<string, ProjectTask>());
  const [confirm, setConfirm] = useState<'generate' | 'publish' | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [historicalTask, setHistoricalTask] = useState<ProjectTask | null>(null);
  const templates = useLandingTemplates();
  const product = useLandingProduct(project.product.id);
  const currentTemplate = templates.data?.templates.find((row) => row.id === project.template_id);
  const canCreateTextTask = !!currentTemplate && !currentTemplate.needs_image;
  const generationBlockReason = !project.can_generate
    ? (project.generation_blocked_reason ?? '当前书籍不能投放')
    : product.isError
      ? '投放产品加载失败，请刷新后重试'
      : !product.data
        ? '正在确认投放产品'
        : !product.data.onelink_base_url
          ? 'Web-only 产品请使用项目的 Web 链接，不支持静态 HTML 生成'
          : !project.template_id
            ? '请先选择 HTML 模板'
            : !project.copy_overrides
              ? '请先保存模板与对应语言完整文案'
              : project.chapters.ready < project.chapters.selected
                ? projectHtmlReadinessReason(project.chapters)
                : !project.tasks.total && !canCreateTextTask
                  ? '当前模板需要先上传素材'
                  : null;
  const tasks = useLandingProjectTasks(project.id, page);
  // 列表按创建时间倒序分页，素材按正序；先按服务端同口径排序算出目标所在页，再翻页高亮。
  useEffect(() => {
    if (!focusTaskId) return;
    let cancelled = false;
    void locateLandingProjectTaskPage(project.id, focusTaskId)
      .then((found) => {
        if (cancelled) return;
        if (found === null) {
          toast('未找到该素材关联的落地页，请刷新后重试', { tone: 'warn' });
          return;
        }
        setPage(found);
        setFocusedTaskId(focusTaskId);
      })
      .catch((error: unknown) => {
        if (!cancelled)
          toast(error instanceof Error ? error.message : '定位落地页失败', { tone: 'neg' });
      });
    return () => {
      cancelled = true;
    };
  }, [project.id, focusTaskId]);
  useEffect(() => {
    if (!focusedTaskId || !tasks.data?.data.some((row) => row.id === focusedTaskId)) return;
    document
      .querySelector(`[data-task-id="${focusedTaskId}"]`)
      ?.scrollIntoView?.({ block: 'center' });
  }, [focusedTaskId, tasks.data]);
  const client = useQueryClient();
  const deliveryBlockReason = !project.can_generate
    ? (project.generation_blocked_reason ?? '当前书籍不能投放')
    : project.status === 'ARCHIVED'
      ? '归档项目不能标记投放'
      : null;
  const delivery = useMutation({
    mutationFn: (input: { task: ProjectTask; status: LandingPageDeliveryStatus }) =>
      landingProjectsApi.setTaskDelivery(project.id, input.task.id, input.status),
    onSuccess: (task) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      toast(task.delivery_status === 'DELIVERING' ? '已标记为投放中' : '已停止投放', {
        tone: 'pos',
      });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const execute = useMutation({
    mutationFn: (input: {
      operation: 'generate' | 'publish';
      retry_failed_only?: boolean;
      task_ids?: string[];
    }) => {
      if (input.operation === 'generate' && generationBlockReason)
        throw new Error(generationBlockReason);
      return landingProjectsApi.execute(project.id, input);
    },
    onSuccess: (execution) => {
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
      onExecution(execution.id);
      toast('后台执行已提交，可关闭详情', { tone: 'info' });
    },
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  const download = async (ids: string[], historical = false) => {
    setDownloading(true);
    try {
      for (const id of ids) {
        const { filename, html } = historical
          ? await landingPagesApi.historicalInlineHtml(id)
          : await landingPagesApi.inlineHtml(id);
        const objectUrl = URL.createObjectURL(
          new Blob([html], { type: 'text/html;charset=utf-8' }),
        );
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      }
      toast(
        historical
          ? '已下载历史 HTML，仅供旧配置对照；新广告请重新生成并下载'
          : '已下载当前版本，请替换之前使用的文件',
        { tone: 'pos' },
      );
    } catch (error) {
      toast(error instanceof Error ? error.message : '下载失败', { tone: 'neg' });
    } finally {
      setDownloading(false);
      void client.invalidateQueries({ queryKey: landingProjectQueryKey });
    }
  };
  const preview = async (id: string) => {
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    try {
      const response = await landingPagesApi.previewUrl(id);
      if (popup) popup.location.href = response.url;
      else toast('浏览器拦截预览窗口，请允许弹窗后重试', { tone: 'warn' });
    } catch (error) {
      popup?.close();
      toast(error instanceof Error ? error.message : '预览失败', { tone: 'neg' });
    }
  };
  const rows = tasks.data?.data ?? [];
  const toggle = (row: ProjectTask) =>
    setSelection((current) => {
      const next = new Map(current);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  useEffect(() => {
    if (!tasks.data) return;
    setSelection((current) => {
      let changed = false;
      const next = new Map(current);
      for (const row of tasks.data.data)
        if (next.has(row.id) && next.get(row.id) !== row) {
          next.set(row.id, row);
          changed = true;
        }
      return changed ? next : current;
    });
  }, [tasks.data]);
  const selectedDownloadable =
    selection.size > 0 && [...selection.values()].every(projectDownloadAllowed);
  const generateDisabledReason =
    generationBlockReason ?? (execute.isPending ? '正在提交任务，请稍候' : null);
  const retryGenerationDisabledReason =
    generateDisabledReason ?? (project.tasks.failed === 0 ? '没有生成失败的落地页' : null);
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="muted">
          {selection.size
            ? `已选 ${selection.size} 个落地页（跨分页保留）`
            : '未选择时生成作用于整个项目的待生成落地页'}
        </span>
        <div style={{ flex: 1 }} />
        {/* 禁用按钮不接收指针事件，由外层承载悬停提示，避免浏览器吞掉 title。 */}
        <span
          title={generateDisabledReason ?? '提交后台生成'}
          style={{
            display: 'inline-flex',
            cursor: generateDisabledReason ? 'not-allowed' : undefined,
          }}
        >
          <button
            type="button"
            className="btn btn-accent"
            disabled={!!generateDisabledReason}
            title={generateDisabledReason ?? undefined}
            style={{ pointerEvents: generateDisabledReason ? 'none' : undefined }}
            onClick={() => setConfirm('generate')}
          >
            {selection.size
              ? '生成已选落地页'
              : !project.tasks.total && canCreateTextTask
                ? '生成纯文字落地页'
                : '生成待更新落地页'}
          </button>
        </span>
        <span
          title={retryGenerationDisabledReason ?? undefined}
          style={{
            display: 'inline-flex',
            cursor: retryGenerationDisabledReason ? 'not-allowed' : undefined,
          }}
        >
          <button
            type="button"
            className="btn"
            disabled={!!retryGenerationDisabledReason}
            title={retryGenerationDisabledReason ?? undefined}
            style={{ pointerEvents: retryGenerationDisabledReason ? 'none' : undefined }}
            onClick={() =>
              execute.mutate({
                operation: 'generate',
                retry_failed_only: true,
                ...(selection.size ? { task_ids: [...selection.keys()] } : {}),
              })
            }
          >
            仅重试生成失败
          </button>
        </span>
        <button
          type="button"
          className="btn"
          disabled={!selectedDownloadable || downloading}
          title={
            selectedDownloadable
              ? '下载所选当前版本 HTML'
              : '请勾选已生成且未过期的落地页；过期产物需重新生成'
          }
          onClick={() => void download([...selection.keys()])}
        >
          下载已选 {selection.size} 项
        </button>
        <button
          type="button"
          className="btn"
          disabled={!selectedDownloadable || execute.isPending}
          onClick={() => setConfirm('publish')}
        >
          发布已选
        </button>
      </div>
      {generationBlockReason && <p className="muted">{generationBlockReason}</p>}
      {tasks.isPending ? (
        <div className="landing-project-state" role="status">
          <Spinner size={16} />
          <span>加载 HTML 落地页…</span>
        </div>
      ) : tasks.isError ? (
        <div className="card landing-project-state" role="alert">
          <Icons.IconAlert size={18} style={{ color: 'var(--neg)' }} />
          <span className="landing-project-state-message">{tasks.error.message}</span>
          <button type="button" className="btn" onClick={() => void tasks.refetch()}>
            重新加载
          </button>
        </div>
      ) : (
        <Table
          className="landing-tasks-table landing-project-tasks-table"
          style={{ minWidth: 1560 }}
        >
          <colgroup>
            <col style={{ width: 40 }} />
            <col style={{ width: 280 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 300 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 220 }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="全选当前页落地页"
                  checked={rows.length > 0 && rows.every((row) => selection.has(row.id))}
                  onChange={(event) =>
                    setSelection((current) => {
                      const next = new Map(current);
                      for (const row of rows)
                        if (event.target.checked) next.set(row.id, row);
                        else next.delete(row.id);
                      return next;
                    })
                  }
                />
              </th>
              <th>落地页 / 素材</th>
              <th>版本</th>
              <th>生成进度</th>
              <th>结果 / 失败原因</th>
              <th>发布</th>
              <th>投放</th>
              <th>下载</th>
              <th className="landing-task-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                data-task-id={row.id}
                aria-current={row.id === focusedTaskId ? 'true' : undefined}
                style={row.id === focusedTaskId ? { background: 'var(--accent-soft)' } : undefined}
              >
                <td>
                  <input
                    type="checkbox"
                    aria-label={`选择落地页 ${row.task_id}`}
                    checked={selection.has(row.id)}
                    onChange={() => toggle(row)}
                  />
                </td>
                <td>
                  <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
                    {row.banner?.r2_key && (
                      <LandingAssetImage
                        r2Key={row.banner.r2_key}
                        name={row.asset_name ?? '投放素材'}
                        compact
                        zoomable
                      />
                    )}
                    <div className="landing-project-task-identity">
                      <CopyableCode value={row.task_id} label="落地页编号" />
                      <div
                        className="landing-project-task-asset-name"
                        title={row.asset_name ?? undefined}
                      >
                        {row.asset_name ?? '纯文字落地页'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="mono">
                  配置 v{row.config_version}
                  <div className="muted mono" title={row.artifact_version ?? undefined}>
                    产物{' '}
                    {row.artifact_version
                      ?.split('/')
                      .filter(Boolean)
                      .at(-1)
                      ?.replaceAll('-', '')
                      .slice(0, 8) ?? '—'}
                  </div>
                </td>
                <td>
                  {row.execution ? (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={() => onExecution(row.execution?.id ?? '')}
                    >
                      <TaskProgressCell task={row} />
                    </button>
                  ) : (
                    <ProjectState status={row.stale_reason ? 'STALE' : row.generate_status} />
                  )}
                  {row.generated_at && (
                    <div className="mono muted" style={{ fontSize: 'var(--text-xs)' }}>
                      {fmtDateTime(row.generated_at)}
                      <div>
                        {row.generated_by ? (row.generated_by.nickname ?? '账号不存在') : '—'} 生成
                      </div>
                    </div>
                  )}
                </td>
                <td className="landing-project-task-result">
                  {row.generate_error && (
                    <p role="alert" style={{ color: 'var(--neg)' }}>
                      {row.generate_error}
                    </p>
                  )}
                  {row.stale_reason && (
                    <div style={{ color: 'var(--warn)', whiteSpace: 'pre-line' }}>
                      {row.stale_reason}
                      {!row.stale_reason.includes('重新下载') && ' · 请重新生成并重新下载'}
                    </div>
                  )}
                  {!row.stale_reason && row.needs_redownload && row.download_count === 0 && (
                    <span className="chip chip-info">新 HTML 已生成，请下载</span>
                  )}
                  {!row.can_download &&
                    row.download_block_reason &&
                    row.download_block_reason !== row.generate_error &&
                    row.download_block_reason !== row.stale_reason && (
                      <div className="muted">{row.download_block_reason}</div>
                    )}
                </td>
                <td className="landing-project-task-publish">
                  {row.publish_status ? <ProjectState status={row.publish_status} /> : '未发布'}
                  {row.published_url && <CopyableCode value={row.published_url} label="发布地址" />}
                </td>
                <td>
                  <LandingPageDeliveryCell
                    task={row}
                    blockedReason={deliveryBlockReason}
                    pending={delivery.isPending && delivery.variables?.task.id === row.id}
                    onChange={(status) => delivery.mutate({ task: row, status })}
                  />
                </td>
                <td>
                  <LandingPageDownloadCell task={row} />
                </td>
                <td className="landing-task-actions">
                  <div className="hstack landing-task-action-buttons" style={{ gap: 4 }}>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!row.artifact_path || !projectDownloadAllowed(row)}
                      onClick={() => void preview(row.id)}
                    >
                      预览
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!projectDownloadAllowed(row) || downloading}
                      title={row.download_block_reason ?? row.stale_reason ?? '下载当前版本'}
                      onClick={() => void download([row.id])}
                    >
                      下载
                    </button>
                    {row.execution && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => onExecution(row.execution?.id ?? '')}
                      >
                        进度 / 结果
                      </button>
                    )}
                    {row.artifact_path &&
                      (row.stale_reason ||
                        row.generated_input_fingerprint !== row.input_fingerprint) && (
                        <ContextMenu
                          align="right"
                          width={240}
                          items={[
                            {
                              label: '下载历史 HTML（旧配置）',
                              disabled: downloading,
                              icon: 'IconDownload',
                              onClick: () => setHistoricalTask(row),
                            },
                          ]}
                        >
                          <button
                            type="button"
                            className="btn btn-sm"
                            aria-label={`更多落地页操作 ${row.task_id}`}
                            title="更多"
                          >
                            <Icons.IconMoreV size={12} />
                          </button>
                        </ContextMenu>
                      )}
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={9}>
                  暂无 HTML
                  落地页。选择模板后，上传素材即创建对应落地页；纯文字模板自动提供无图落地页。
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      <Pagination total={tasks.data?.total ?? 0} page={page} pageSize={20} onChange={setPage} />
      {confirm && (
        <ConfirmDialog
          title={confirm === 'generate' ? '后台生成 HTML' : '发布当前版本'}
          message={
            confirm === 'generate'
              ? `${selection.size ? `本次处理已选 ${selection.size} 个落地页` : `本次处理项目内待更新落地页，共 ${project.tasks.total || (canCreateTextTask ? 1 : 0)} 个`}。生成成功后请重新下载 HTML；之前的下载文件不会自动更新。`
              : `发布已选 ${selection.size} 个落地页的当前产物，线上版本将更新。`
          }
          confirmLabel={confirm === 'generate' ? '提交生成' : '发布'}
          onConfirm={() =>
            execute.mutate({
              operation: confirm,
              ...(selection.size ? { task_ids: [...selection.keys()] } : {}),
            })
          }
          onClose={() => setConfirm(null)}
        />
      )}
      {historicalTask && (
        <ConfirmDialog
          title="下载历史 HTML（旧配置）？"
          message="仅下载最后保留的成功历史产物，供旧配置对照；这不是当前项目配置，新广告请重新生成并下载当前 HTML。"
          details={['不会触发生成、AI 审核或 AI 脱敏。默认下载入口仍禁止旧产物。']}
          confirmLabel="仅下载历史文件"
          onConfirm={() => void download([historicalTask.id], true)}
          onClose={() => setHistoricalTask(null)}
        />
      )}
    </div>
  );
}

/** 行内优先使用对应任务的真实子项进度，批次摘要与单任务进度不混淆。 */
function TaskProgressCell({ task }: { task: ProjectTask }) {
  const execution = task.execution;
  if (!execution) return null;
  const item = execution.items.find((row) => row.task_id === task.id);
  return (
    <div className="vstack" style={{ gap: 5, textAlign: 'left', width: '100%' }}>
      <ProjectState status={item?.status ?? execution.status} />
      <span>{projectPhaseLabel(item?.phase ?? execution.phase)}</span>
      <span className="mono muted">
        {item ? '本任务' : '所属批次'} {item?.completed ?? execution.completed}/
        {item?.total ?? execution.total}
      </span>
      {(item?.total ?? execution.total) > 0 && (
        <progress
          className="landing-project-progress"
          aria-label="任务实际完成进度"
          value={item?.completed ?? execution.completed}
          max={item?.total ?? execution.total}
        />
      )}
    </div>
  );
}
